import { createAuthClient } from 'better-auth/react';
import { twoFactorClient, usernameClient, inferAdditionalFields } from 'better-auth/client/plugins';
import { passkeyClient } from '@better-auth/passkey/client';

/**
 * The client for the ACCOUNT deployment of the auth service.
 *
 * ── Which origin, and why it is not the hot path ─────────────────────
 *
 * `VITE_AUTH_URL` is the account deployment (`SERVICE_ROLE=account`). ADR 0002
 * settled that the account portal is a third deployment rather than a client of
 * the admin API — the admin API answers on the strength of an admin role, so a
 * service calling it for end users is a confused deputy by construction.
 *
 * That deployment serves the tightest allow-list of the three: profile,
 * credentials, MFA, this person's own sessions, agreements and their own
 * activity. It REFUSES sign-in, sign-up and the OAuth provider endpoints, which
 * is why `VITE_LOGIN_URL` below is a different origin and not a path here.
 *
 * ── The session is read cross-origin, and that works on purpose ──────
 *
 * The cookie is scoped to the parent domain (`COOKIE_DOMAIN=.ridgewill.com`),
 * so a session created by the hosted login on `auth-dev` is readable here.
 * Verified, not assumed: a cookie set on auth-dev resolves on a sibling
 * subdomain. `credentials: 'include'` is what makes the browser send it, and
 * this origin is in the service's TRUSTED_ORIGINS so CORS permits the reply.
 */
const BASE = import.meta.env.VITE_AUTH_URL ?? '';

/** Where somebody goes to SIGN IN. A different origin, deliberately — the
 *  account deployment refuses `/sign-in/email`, so a link pointing here would
 *  render a form that cannot submit. */
export const LOGIN_URL = import.meta.env.VITE_LOGIN_URL ?? '';

export const authClient = createAuthClient({
  baseURL: BASE,
  basePath: '/api/auth',
  fetchOptions: { credentials: 'include' },
  plugins: [
    twoFactorClient(),
    usernameClient(),
    /**
     * Passkeys (RidgeWill/account#15). Adds `passkey.listUserPasskeys()`,
     * `passkey.deletePasskey()` and `passkey.updatePasskey()`.
     *
     * Registration is NOT offered here. The hosted login already does it
     * (RidgeWill/auth#153) and it needs the `navigator.credentials` prompt on
     * the origin the credential will be bound to — this portal is a different
     * origin, so a passkey created here would be registered against the wrong
     * relying party and would not work at sign-in.
     */
    passkeyClient(),
    /**
     * `role` and `pendingAgreements` are server-owned additional fields on the
     * user object. Without this the client's types do not know they exist and
     * every read of them is an `any` cast at the call site.
     */
    inferAdditionalFields({
      user: {
        role: { type: 'string' },
        pendingAgreements: { type: 'string[]' },
      },
    }),
  ],
});

export const useSession = authClient.useSession;
